import { Table, Typography } from '@app/components';
import { useTableColumns } from './useTableColumns';
import { useBasicQueryControls } from '@app/hooks/useBasicQueryControls.hook';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { useGetTeamMembers } from '../api/team.queries';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { useTranslation } from 'react-i18next';
import { GET_USERS_QUERY } from '@app/constants/query-api-configs';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { ArrayParam } from '@app/utils/use-query-params.utils';

export const TeamTable: React.FC<{ isLoading?: boolean }> = ({ isLoading }) => {
  const { t } = useTranslation();

  const {
    values: { page, sort = 'user.email,asc', size },
    handlers: { handlePageChange, handleSortChange, handleSizeChange },
  } = useBasicQueryControls({
    initialValues: {
      sort: 'user.email,asc',
      page: 1,
      size: 10,
    },
    prefix: GET_USERS_QUERY.tablePrefix,
  });

  const [term] = useQueryParam('term', withDefault(StringParam, ''));
  const [statuses] = useQueryParam('statuses', withDefault(ArrayParam, []));
  const [roles] = useQueryParam('roles', withDefault(ArrayParam, []));
  const [fields] = useQueryParam('fields', withDefault(StringParam, ''));

  const { currentUser } = useAuthorizedUser();
  const teamId = currentUser?.teamId;

  const {
    data: teamMembers,
    isLoading: isTeamMembersLoading,
    isFetching,
  } = useGetTeamMembers(teamId ?? '', {
    page: Math.ceil((page || 1) - 1),
    size,
    sort,
    term,
    statuses: statuses.length > 0 ? statuses : undefined,
    roles: roles.length > 0 ? roles : undefined,
    fields: fields || undefined,
  });

  const columns = useTableColumns();

  // Total counter
  // const leftContent = useMemo(() => {
  //   return (
  //     <Typography variant="xl" className="font-bold">
  //       {t('team.teamMembersSection.title')} ({teamMembers?.total || 0})
  //     </Typography>
  //   );
  // }, [teamMembers?.total, t]);

  const isLoadingState = isLoading || isTeamMembersLoading;

  if (isLoadingState) {
    return (
      <div className="mb-6">
        <ContentLoader loading={isLoadingState} />
      </div>
    );
  }

  if (!teamMembers?.result || teamMembers.result.length === 0) {
    return (
      <div className="px-6 pb-6">
        <Typography variant="s" className="text-gray-500">
          {t('team.teamMembersSection.noTeamMembersMessage')}
        </Typography>
      </div>
    );
  }

  return (
    <Table
      cols={columns}
      tableData={teamMembers?.result}
      loading={isLoading || isFetching}
      notFoundMessage={t('team.teamMembersSection.noTeamMembersMessage')}
      headerNameClassName="font-bold text-xs text-gray-800"
      firstColumnHeaderClassName="pl-6"
      currentPage={page}
      pageSize={size}
      onSizeChange={handleSizeChange}
      total={teamMembers?.total || 0}
      onPagination={handlePageChange}
      currentSort={sort}
      onSortChanged={handleSortChange}
      // leftHeaderContent={leftContent}
      hidePageSize={false}
    />
  );
};
